import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { useScopedPath } from "@/lib/scoped";
import {
  Receipt,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  ArrowLeft,
} from "lucide-react";

function formatMoney(pence, currency) {
  if (typeof pence !== "number") return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "gbp").toUpperCase(),
    }).format(pence / 100);
  } catch {
    return `${(pence / 100).toFixed(2)} ${(currency || "gbp").toUpperCase()}`;
  }
}

function formatMatchDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ paymentStatus, status }) {
  if (paymentStatus === "paid") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold uppercase tracking-wider"
        data-testid="entry-status-paid"
      >
        <CheckCircle2 className="h-3 w-3" /> Paid
      </span>
    );
  }
  if (paymentStatus === "failed" || status === "expired") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-red-50 border border-red-200 text-red-800 text-[10px] font-bold uppercase tracking-wider"
        data-testid="entry-status-failed"
      >
        <XCircle className="h-3 w-3" /> Failed
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-wider"
      data-testid="entry-status-pending"
    >
      <Clock className="h-3 w-3" /> Pending
    </span>
  );
}

export default function MyEntriesPage() {
  const scope = useScopedPath();
  const navigate = useNavigate();
  const [state, setState] = useState({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/payments/transactions");
        if (!cancelled) setState({ kind: "ready", rows: data || [] });
      } catch (err) {
        if (!cancelled)
          setState({ kind: "error", message: formatApiError(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = state.kind === "ready" ? state.rows : [];
  const paidCount = rows.filter((r) => r.payment_status === "paid").length;
  const totalPencePaid = rows
    .filter((r) => r.payment_status === "paid")
    .reduce((s, r) => s + (r.amount_pence || 0), 0);
  const currency = rows.find((r) => r.currency)?.currency || "gbp";

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
        data-testid="my-entries-page"
      >
        <button
          type="button"
          onClick={() => navigate(scope("/"))}
          className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-950 mb-4"
          data-testid="my-entries-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to predictions
        </button>

        <header className="mb-8">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-zinc-950 text-white">
              <Receipt className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                Your history
              </p>
              <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">
                My entries
              </h1>
            </div>
          </div>
        </header>

        {/* Summary strip */}
        {state.kind === "ready" && rows.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 mb-8">
            <div className="rounded-sm border border-zinc-200 p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Match-days paid
              </p>
              <p
                className="mt-1 font-heading text-3xl font-bold text-zinc-950 tabular-nums"
                data-testid="my-entries-paid-count"
              >
                {paidCount}
              </p>
            </div>
            <div className="rounded-sm border border-zinc-200 p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Total spent
              </p>
              <p
                className="mt-1 font-heading text-3xl font-bold text-zinc-950 tabular-nums"
                data-testid="my-entries-total-spent"
              >
                {formatMoney(totalPencePaid, currency)}
              </p>
            </div>
          </div>
        ) : null}

        {state.kind === "loading" ? (
          <div
            className="flex items-center justify-center py-24"
            data-testid="my-entries-loading"
          >
            <Loader2 className="h-8 w-8 text-zinc-400 animate-spin" />
          </div>
        ) : state.kind === "error" ? (
          <div className="border border-red-200 bg-red-50 rounded-sm p-6 text-sm text-red-800">
            {state.message}
          </div>
        ) : rows.length === 0 ? (
          <div
            className="border border-dashed border-zinc-300 rounded-sm p-12 text-center"
            data-testid="my-entries-empty"
          >
            <Receipt className="mx-auto h-10 w-10 text-zinc-400" />
            <p className="mt-4 font-heading text-xl font-bold text-zinc-950">
              No entries yet.
            </p>
            <p className="mt-2 text-sm text-zinc-500 max-w-md mx-auto">
              When you enter a paid match-day, your receipts and match history
              will show up here.
            </p>
            <button
              type="button"
              onClick={() => navigate(scope("/"))}
              className="mt-6 h-10 px-5 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold text-sm"
            >
              Go to predictions
            </button>
          </div>
        ) : (
          <ul
            className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm bg-white"
            data-testid="my-entries-list"
          >
            {rows.map((r) => {
              const testId = `entry-row-${r.id || r.session_id}`;
              return (
                <li
                  key={r.id || r.session_id}
                  className="p-4 sm:p-5 flex items-center justify-between gap-4"
                  data-testid={testId}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading text-base font-bold text-zinc-950">
                        {formatMatchDate(r.applied_match_date || r.match_date)}
                      </p>
                      <StatusPill
                        paymentStatus={r.payment_status}
                        status={r.status}
                      />
                      {r.credit_consumed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-zinc-100 text-zinc-700 text-[10px] font-bold uppercase tracking-wider">
                          Credit used
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-zinc-500 tabular-nums">
                      #{(r.session_id || "").slice(-8).toUpperCase() || "—"}
                      {r.created_at ? (
                        <>
                          <span className="mx-1.5">·</span>
                          {formatWhen(r.created_at)}
                        </>
                      ) : null}
                    </p>
                  </div>
                  <p className="font-heading text-lg font-bold text-zinc-950 tabular-nums whitespace-nowrap">
                    {formatMoney(r.amount_pence, r.currency)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
